import { useSelector } from "react-redux";
import { Box, Button, Stepper, Step, StepLabel } from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import { shades } from "../../theme";
import Payment from "./Payment";
import Shipping from "./Shipping";
import { loadStripe } from "@stripe/stripe-js";

const stripePromise = loadStripe(
  "pk_test_51OPpYOSF01MmEgFzwKtafOb8ZvgNl19ifexLmQaU1XUFdXsEwwN6dkIJMLOZxzeIgRrEZ3x3DWiCQ8qsI10UFlZc00kpOSSo6J"
);

const Checkout = () => {
  const [activeStep, setActiveStep] = useState(0);
  const cart = useSelector((state) => state.cart.cart);
  const isFirstStep = activeStep === 0;
  const isSecondStep = activeStep === 1;

  const handleFormSubmit = async (values, actions) => {

    const requestBodyNew = {
      firstName :values.billingAddress.firstName,
      lastName : values.billingAddress.lastName,
      email : values.email,
      phoneNumber : values.phoneNumber,
      street1 : values.billingAddress.street1,
      street2 : values.billingAddress.street2,
      city : values.billingAddress.city,
      state : values.billingAddress.state,
      country : values.billingAddress.country,
      zipCode : values.billingAddress.zipCode,
    }
    console.log("values", requestBodyNew);
    setActiveStep(activeStep + 1);

    // this copies the billing address onto shipping address
    if (isFirstStep && values.shippingAddress.isSameAddress) {
      actions.setFieldValue("shippingAddress", {
        ...values.billingAddress,
        isSameAddress: true,
      });
    }

    if (isSecondStep) {
      makePayment(values);
    }

    actions.setTouched({});
  };

  async function makePayment(values) {
    const stripe = await stripePromise;
    const requestBody = {
      firstName :values.billingAddress.firstName,
      lastName : values.billingAddress.lastName,
      email : values.email,
      phoneNumber : values.phoneNumber,
      street1 : values.billingAddress.street1,
      street2 : values.billingAddress.street2,
      city : values.billingAddress.city,
      state : values.billingAddress.state,
      country : values.billingAddress.country,
      zipCode : values.billingAddress.zipCode,
      userName:values.email,
      products: cart.map(({ _id, count, price, name }) => ({
        _id,
        count,
        price,
        name
      })),
      productName: cart.map(({ name }) => String(name)).join(","),
      productCount: cart.map(({ count }) => String(count)).join(","),
    };

    const userReqData = {
      data: {
      firstName: values.billingAddress.firstName,
      lastName: values.billingAddress.lastName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      street1: values.billingAddress.street1,
      street2: values.billingAddress.street2,
      city: values.billingAddress.city,
      state: values.billingAddress.state,
      country: values.billingAddress.country,
      zipCode: values.billingAddress.zipCode,
      }
    }

    const response = await fetch(`https://blackcms.onrender.com/api/data/${"aerio_orders"}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });



    for (const item of cart) {
      const updateResponse = await fetch(`https://blackcms.onrender.com/api/data/${"aerio_product"}/${item._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            active: false,
        }),
      });
  
      const updateData = await updateResponse.json();
      console.log(`Updated product ${item.id}:`, updateData);
    }

    const stripeResponse = await fetch(`https://blackcms.onrender.com/api/data/stripe-payment/${"aerio_payment"}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    const session = await stripeResponse.json();
    console.log(session.data.sessionId)

    await stripe.redirectToCheckout({
      sessionId: session.data.sessionId,
    });
  }

  return (
    <Box width="80%" m="100px auto">
      <Stepper activeStep={activeStep} sx={{ m: "20px 0" }}>
        <Step>
          <StepLabel>Billing</StepLabel>
        </Step>
        <Step>
          <StepLabel>Payment</StepLabel>
        </Step>
      </Stepper>
      <Box>
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={checkoutSchema[activeStep]}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              {isFirstStep && (
                <Shipping
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                />
              )}
              {isSecondStep && (
                <Payment
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                />
              )}
              <Box display="flex" justifyContent="space-between" gap="50px">
                {!isFirstStep && (
                  <Button
                    fullWidth
                    color="primary"
                    variant="contained"
                    sx={{
                      backgroundColor: shades.primary[200],
                      boxShadow: "none",
                      color: "white",
                      borderRadius: 0,
                      padding: "15px 40px",
                    }}
                    onClick={() => setActiveStep(activeStep - 1)}
                  >
                    Back
                  </Button>
                )}
                <Button
                  fullWidth
                  type="submit"
                  color="primary"
                  variant="contained"
                  sx={{
                    backgroundColor: shades.primary[400],
                    boxShadow: "none",
                    color: "white",
                    borderRadius: 0,
                    padding: "15px 40px",
                  }}
                >
                  {!isSecondStep ? "Next" : "Place Order"}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

const initialValues = {
  billingAddress: {
    firstName: "",
    lastName: "",
    country: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
  },
  shippingAddress: {
    isSameAddress: true,
    firstName: "",
    lastName: "",
    country: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
  },
  email: "",
  phoneNumber: "",
};

const checkoutSchema = [
  yup.object().shape({
    billingAddress: yup.object().shape({
      firstName: yup.string().required("required"),
      lastName: yup.string().required("required"),
      country: yup.string().required("required"),
      street1: yup.string().required("required"),
      street2: yup.string(),
      city: yup.string().required("required"),
      state: yup.string().required("required"),
      zipCode: yup.string().required("required"),
    }),
    shippingAddress: yup.object().shape({
      isSameAddress: yup.boolean(),
      firstName: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      lastName: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      country: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      street1: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      street2: yup.string(),
      city: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      state: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      zipCode: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
    }),
  }),
  yup.object().shape({
    email: yup.string().required("required"),
    phoneNumber: yup.string().required("required"),
  }),
];

export default Checkout;
